import { useEffect } from "react";
import { usePrevious } from "react-use";
import {
  ActionButtonGroup,
  Banner,
  Box,
  Button,
  CheckboxField,
  DropdownOption,
  FormRow,
  FormSection,
  InlineMessage,
  Space,
  useForm,
  validators,
} from "@dbn/omnichannel-design-system-g4p";
import { boolean, either, option, taskEither } from "fp-ts";
import {
  constant,
  constFalse,
  constNull,
  constTrue,
  flow,
  pipe,
} from "fp-ts/function";
import { Option } from "fp-ts/Option";
import { NonEmptyString } from "io-ts-types/lib/NonEmptyString";
import { Tenant, foldTenant } from "../../../globalDomain";
import { LocaleKey, useFormatMessage } from "../../../intl";
import { useCommand } from "../../../useAPI";
import { OptionalEditMoneyField } from "../../../Common/OptionalEditFields/OptionalEditMoneyField";
import { OptionalEditDropdownField } from "../../../Common/OptionalEditFields/OptionalEditDropdownField";
import { OptionalEditTextField } from "../../../Common/OptionalEditFields/OptionalEditTextField";
import {
  dropdownOptionToValue,
  selectedDropdownOption,
} from "../../../Common/selectDropdownOption";
import { useTenantCurrency } from "../../../Common/useTenantCurrency";
import { useValidators } from "../../../Common/useValidators";
import { AmountLimit } from "../../api";
import { Overdraft, ReworkOverdraft } from "../../domain";
import {
  liabilitiesAmountLimits,
  SelectedApplicant,
} from "../../mortgageDashboardUtils";
import { useAppContext } from "../../../useAppContext";
import { checkRequiredFieldsProvided, resolveAmount, toFalse } from "./utils";
import * as api from "./api";
import { LiabilitiesVariant } from "./Liabilities";
import * as ovdApi from "../../../PreapprovedOverdraft/ExpensesPage/ExpensesConfirmation/CurrentLiabilitiesDialog/api";
import * as standardLoanApi from "../../../StandardLoan/ExpensesAndAdditionalIncome/ExpensesConfirmation/CurrentLiabilitiesDialog/api";
import { foldLiabilitiesVariant } from "../../../StandardLoan/domain";
import { useFormatReworkData } from "../../useFormatReworkData";

type Props = {
  selectedApplicant: SelectedApplicant;
  initialValues: Option<Overdraft>;
  onSave: (loan: Overdraft) => unknown;
  onCancel: () => unknown;
  onEdit: () => unknown;
  onRemove: () => unknown;
  isACPhase: boolean;
  isInteractingWithAnyForm: boolean;
  isViewMode: boolean;
  providersList: DropdownOption<NonEmptyString>[];
  amountLimits: {
    [key in "ccAndOvdCurrentBalance" | "ccAndOvdLimit"]: AmountLimit;
  };
  edit: boolean;
  variant: LiabilitiesVariant;
  reworkOverdraft: Option<ReworkOverdraft>;
};

type FormState = {
  currentBalance: Option<number>;
  overdraftLimit: Option<number>;
  repaidBeforeDisbursement: Option<boolean>;
  changeLimit: Option<boolean>;
  provider: Option<NonEmptyString>;
  contractNumber: Option<string>;
  newOverdraftLimit: Option<number>;
};

type FormSetup = {
  tenant: Tenant;
  isACPhase: boolean;
  edit: boolean;
  manuallyAdded: boolean;
};

function formStateFromInitialValues(
  initialValues: Option<Overdraft>
): FormState {
  return pipe(
    initialValues,
    option.fold(
      () => ({
        currentBalance: option.none,
        overdraftLimit: option.none,
        repaidBeforeDisbursement: option.none,
        changeLimit: option.none,
        provider: option.none,
        contractNumber: option.none,
        newOverdraftLimit: option.none,
      }),
      values => ({
        currentBalance: resolveAmount(values.currentBalance),
        overdraftLimit: resolveAmount(values.overdraftLimit),
        repaidBeforeDisbursement: values.repaidBeforeDisbursement,
        changeLimit: values.changeLimit,
        provider: values.provider,
        contractNumber: values.contractNumber,
        newOverdraftLimit: resolveAmount(values.newOverdraftLimit),
      })
    )
  );
}

const getChangeLimitDisbursementConflict = (values: FormState) =>
  toFalse(values.changeLimit) && toFalse(values.repaidBeforeDisbursement);

const getDisplayedFields = (
  values: FormState,
  formSetup: FormSetup,
  variant: LiabilitiesVariant
): { [K in keyof FormState]: boolean } => ({
  currentBalance: true,
  overdraftLimit: true,
  repaidBeforeDisbursement: foldLiabilitiesVariant(variant, {
    onMtg: constTrue,
    onCf: constFalse,
    onOvd: constFalse,
  }),
  changeLimit: foldLiabilitiesVariant(variant, {
    onMtg: () => formSetup.tenant === "CZ",
    onCf: constFalse,
    onOvd: constFalse,
  }),
  provider:
    !getChangeLimitDisbursementConflict(values) &&
    (formSetup.edit || option.isSome(values.provider)) &&
    (toFalse(values.changeLimit) || toFalse(values.repaidBeforeDisbursement)) &&
    variant === "MTG",
  contractNumber:
    !getChangeLimitDisbursementConflict(values) &&
    formSetup.tenant === "CZ" &&
    (toFalse(values.changeLimit) || toFalse(values.repaidBeforeDisbursement)) &&
    variant === "MTG",
  newOverdraftLimit:
    !getChangeLimitDisbursementConflict(values) &&
    formSetup.tenant === "CZ" &&
    toFalse(values.changeLimit),
});

const getRequiredFields = (
  values: FormState,
  formSetup: FormSetup,
  variant: LiabilitiesVariant
): { [K in keyof FormState]: boolean } => ({
  ...getDisplayedFields(values, formSetup, variant),
  ...(!formSetup.isACPhase
    ? {
        provider: false,
        contractNumber: false,
      }
    : {}),
});

export function OverdraftForm(props: Props) {
  const formatMessage = useFormatMessage();
  const {
    apiParameters: { tenant },
    config: { r8EnableSimpleRefinancing, enableCfRepaidLoans },
  } = useAppContext();
  const {
    defined,
    definedNoExtract,
    nonNegativeNumber,
    amountMinMax,
    maxLength,
    amountMinMaxWithNegative,
  } = useValidators();
  const currency = useTenantCurrency();

  const addOverdraft = useCommand(api.addOverdraft);
  const editOverdraft = useCommand(api.editOverdraft);
  const addOverdraftStandardLoan = useCommand(standardLoanApi.addOverdraft);
  const addOverdraftOverdraft = useCommand(ovdApi.addOverdraft);
  const editOverdraftStandardLoan = useCommand(standardLoanApi.editOverdraft);
  const editOverdraftOverdraft = useCommand(ovdApi.editOverdraft);
  const submitRepaidLoanStandardLoan = useCommand(
    standardLoanApi.submitRepaidLoanStandardLoan
  );

  const {
    formatIssues: formatOverdraftIssues,
    formatFeedback: formatOverdraftFeedback,
  } = useFormatReworkData(props.reworkOverdraft);

  const currentBalance = pipe(
    props.initialValues,
    option.fold(
      () => option.none,
      a => a.currentBalance
    )
  );
  const overDraftLimit = pipe(
    props.initialValues,
    option.fold(
      () => option.none,
      a => a.overdraftLimit
    )
  );

  useEffect(() => {
    if (
      (r8EnableSimpleRefinancing && !option.isSome(currentBalance)) ||
      !option.isSome(overDraftLimit)
    ) {
      props.onEdit();
    }
  }, [currentBalance, overDraftLimit]);

  const formSetup = pipe(
    props.initialValues,
    option.fold(
      (): FormSetup => ({
        tenant,
        edit: props.edit,
        isACPhase: props.isACPhase,
        manuallyAdded: true,
      }),
      values => ({
        tenant,
        edit: props.edit,
        isACPhase: props.isACPhase,
        manuallyAdded: values.manuallyAdded,
      })
    )
  );

  const { fieldProps, handleSubmit, setValues, values } = useForm(
    {
      initialValues: formStateFromInitialValues(props.initialValues),
      fieldValidators: function (values) {
        const requiredFields = getRequiredFields(
          values,
          formSetup,
          props.variant
        );
        return {
          provider: requiredFields.provider ? definedNoExtract() : undefined,
          currentBalance: validators.inSequence(
            defined(),
            nonNegativeNumber,
            amountMinMax(
              props.amountLimits.ccAndOvdCurrentBalance.min,
              props.amountLimits.ccAndOvdCurrentBalance.max
            )
          ),
          overdraftLimit: validators.inSequence(
            defined(),
            nonNegativeNumber,
            amountMinMax(
              props.amountLimits.ccAndOvdLimit.min,
              props.amountLimits.ccAndOvdLimit.max
            )
          ),
          repaidBeforeDisbursement: requiredFields.repaidBeforeDisbursement
            ? definedNoExtract()
            : undefined,
          changeLimit: requiredFields.repaidBeforeDisbursement
            ? validators.inSequence(
                definedNoExtract(),
                validators.fromPredicate(
                  () => !getChangeLimitDisbursementConflict(values),
                  formatMessage(
                    "Mortgage.CBResults.Liabilities.overdraftChangeLimitError"
                  )
                )
              )
            : undefined,
          newOverdraftLimit: requiredFields.newOverdraftLimit
            ? validators.inSequence(
                defined(),
                amountMinMaxWithNegative(
                  liabilitiesAmountLimits.min,
                  liabilitiesAmountLimits.max
                ),
                validators.validator(flow(option.some, either.right))
              )
            : undefined,
          contractNumber: requiredFields.contractNumber
            ? validators.inSequence(
                definedNoExtract(),
                validators.validateIfDefined(maxLength(200))
              )
            : validators.validateIfDefined(maxLength(200)),
        };
      },
    },
    {
      onSubmit: values =>
        pipe(
          {
            applicantIndex: props.selectedApplicant.index,
            currentBalance: { currency, amount: values.currentBalance },
            overdraftLimit: { currency, amount: values.overdraftLimit },
            repaidBeforeDisbursement: values.repaidBeforeDisbursement,
            changeLimit: values.changeLimit,
            provider: values.provider,
            contractNumber: values.contractNumber,
            newOverdraftLimit: pipe(
              values.newOverdraftLimit,
              option.map(amount => ({ currency, amount }))
            ),
          },
          taskEither.of,
          taskEither.chain(newValues =>
            pipe(
              props.initialValues,
              option.fold(
                () =>
                  pipe(
                    foldLiabilitiesVariant(props.variant, {
                      onMtg: () =>
                        addOverdraft({ ...newValues, manuallyAdded: true }),
                      onCf: () =>
                        addOverdraftStandardLoan({
                          currentBalance: newValues.currentBalance,
                          overdraftLimit: newValues.overdraftLimit,
                          repaidBeforeDisbursement:
                            newValues.repaidBeforeDisbursement,
                        }),
                      onOvd: () =>
                        addOverdraftOverdraft({
                          currentBalance: newValues.currentBalance,
                          overdraftLimit: newValues.overdraftLimit,
                        }),
                    }),
                    taskEither.chain(overdraft =>
                      taskEither.fromIO(() => props.onSave(overdraft))
                    )
                  ),
                initialValues =>
                  pipe(
                    foldLiabilitiesVariant(props.variant, {
                      onMtg: () =>
                        editOverdraft({
                          ...newValues,
                          manuallyAdded: formSetup.manuallyAdded,
                          recordId: initialValues.recordId,
                        }),
                      onCf: () =>
                        editOverdraftStandardLoan({
                          currentBalance: newValues.currentBalance,
                          overdraftLimit: newValues.overdraftLimit,
                          recordId: initialValues.recordId,
                          repaidBeforeDisbursement:
                            initialValues.repaidBeforeDisbursement,
                        }),
                      onOvd: () =>
                        editOverdraftOverdraft({
                          currentBalance: newValues.currentBalance,
                          overdraftLimit: newValues.overdraftLimit,
                          recordId: initialValues.recordId,
                        }),
                    }),
                    taskEither.chain(overdraft =>
                      taskEither.fromIO(() => props.onSave(overdraft))
                    )
                  )
              )
            )
          )
        ),
    }
  );

  const previousEdit = usePrevious(formSetup.edit);
  useEffect(() => {
    if (!formSetup.edit || formSetup.edit === previousEdit) return;

    setValues({
      repaidBeforeDisbursement: pipe(
        values.repaidBeforeDisbursement,
        toFalse,
        option.some
      ),
      changeLimit: pipe(values.changeLimit, toFalse, option.some),
    });
  }, [formSetup, previousEdit, values, setValues]);

  const handleCancel = () => {
    pipe(props.initialValues, formStateFromInitialValues, setValues);
    props.onCancel();
  };

  const providerFieldProps = fieldProps("provider");
  const providerOnChange = (value: Option<DropdownOption<NonEmptyString>>) => {
    providerFieldProps.onChange(dropdownOptionToValue(value));
  };

  const displayedFields = getDisplayedFields(values, formSetup, props.variant);

  const displayProductIsUsed = () => {
    if (props.variant !== "MTG" && r8EnableSimpleRefinancing) {
      return <></>;
    }

    if (
      r8EnableSimpleRefinancing &&
      toFalse(
        pipe(
          props.initialValues,
          option.chain(i => i.includedInBonita)
        )
      )
    ) {
      return (
        <InlineMessage
          type="informative"
          size="medium"
          message={formatMessage(
            "Mortgage.CBResults.Liabilities.loan.includedInBonita"
          )}
        />
      );
    } else {
      return (
        <InlineMessage
          type="informative"
          size="medium"
          message={formatMessage(
            "Mortgage.CBResults.Liabilities.loan.notIncludedInBonita"
          )}
        />
      );
    }
  };

  const removeUCBProviderCode = (
    providersList: DropdownOption<NonEmptyString>[],
    manuallyAdded: boolean
  ) => {
    return !manuallyAdded
      ? providersList
      : providersList.filter(
          provider =>
            provider.value !=
            foldTenant(
              tenant,
              () => "1111",
              () => "2700"
            )
        );
  };

  const repaidEditable = pipe(
    props.initialValues,
    option.map(({ repaidEditable }) => repaidEditable),
    option.getOrElse(constFalse)
  );

  const showRepaidCheckboxWhenNotEditable =
    enableCfRepaidLoans &&
    // displayedFields.repaidBeforeDisbursement &&
    props.variant === "CF" &&
    repaidEditable &&
    !formSetup.edit;

  return (
    <FormSection>
      {(displayedFields.currentBalance || displayedFields.overdraftLimit) && (
        <FormRow type="1-1">
          {displayedFields.currentBalance ? (
            <OptionalEditMoneyField
              {...fieldProps("currentBalance")}
              min={props.amountLimits.ccAndOvdCurrentBalance.min}
              max={props.amountLimits.ccAndOvdCurrentBalance.max}
              currency={currency}
              label={formatMessage(
                "Liabilities.AddConsumerLoanOrMortgage.currentBalanceLabel"
              )}
              placeholder={formatMessage(
                "Liabilities.AddConsumerLoanOrMortgage.currentBalanceLabel"
              )}
              edit={
                (formSetup.edit && formSetup.manuallyAdded) ||
                (r8EnableSimpleRefinancing &&
                  !option.isSome(fieldProps("currentBalance").value))
              }
              issues={formatOverdraftIssues(
                "currentBalance",
                fieldProps("currentBalance").issues
              )}
              feedback={formatOverdraftFeedback("currentBalance")}
            />
          ) : null}
          {displayedFields.overdraftLimit ? (
            <OptionalEditMoneyField
              {...fieldProps("overdraftLimit")}
              min={props.amountLimits.ccAndOvdLimit.min}
              max={props.amountLimits.ccAndOvdLimit.max}
              currency={currency}
              label={formatMessage(
                "Liabilities.AddOverdraft.overdraftLimitLabel"
              )}
              placeholder={formatMessage(
                "Liabilities.AddOverdraft.overdraftLimitLabel"
              )}
              edit={
                (formSetup.edit && formSetup.manuallyAdded) ||
                (r8EnableSimpleRefinancing &&
                  !option.isSome(fieldProps("overdraftLimit").value))
              }
              issues={formatOverdraftIssues(
                "overdraftLimit",
                fieldProps("overdraftLimit").issues
              )}
              feedback={formatOverdraftFeedback("overdraftLimit")}
            />
          ) : null}
        </FormRow>
      )}

      {((displayedFields.repaidBeforeDisbursement && formSetup.edit) ||
        showRepaidCheckboxWhenNotEditable) && (
        <FormRow type="full">
          <CheckboxField
            {...fieldProps("repaidBeforeDisbursement")}
            label={formatMessage(
              `${
                props.variant === "CF" ? "StandardLoan" : "Mortgage"
              }.CBResults.Liabilities.repaidBeforeDisbursement` as LocaleKey
            )}
            value={toFalse(fieldProps("repaidBeforeDisbursement").value)}
            onChange={(newValue: boolean) =>
              showRepaidCheckboxWhenNotEditable &&
              option.isSome(props.initialValues)
                ? pipe(
                    submitRepaidLoanStandardLoan({
                      recordId: props.initialValues.value.recordId,
                      repaidBeforeDisbursement: newValue,
                      liabilityType: "OVERDRAFT",
                      manuallyAdded: formSetup.manuallyAdded,
                    }),
                    taskEither.bimap(
                      constant(!newValue), //if network fails consider it reset
                      constant(newValue)
                    ),
                    taskEither.map(repaid =>
                      fieldProps("repaidBeforeDisbursement").onChange(
                        option.some(repaid)
                      )
                    )
                  )()
                : pipe(
                    newValue,
                    option.some,
                    fieldProps("repaidBeforeDisbursement").onChange
                  )
            }
            issues={formatOverdraftIssues(
              "repaidBeforeDisbursement",
              fieldProps("repaidBeforeDisbursement").issues
            )}
          />
        </FormRow>
      )}

      {displayedFields.changeLimit && formSetup.edit && (
        <FormRow type="full">
          <CheckboxField
            {...fieldProps("changeLimit")}
            label={formatMessage("Mortgage.CBResults.Liabilities.changeLimit")}
            description={formatMessage(
              "Mortgage.CBResults.Liabilities.toBeProvidedNote.overdraft"
            )}
            value={toFalse(fieldProps("changeLimit").value)}
            onChange={(newValue: boolean) =>
              pipe(newValue, option.some, fieldProps("changeLimit").onChange)
            }
          />
        </FormRow>
      )}

      {getChangeLimitDisbursementConflict(values) && (
        <Banner
          type="error"
          content={formatMessage(
            "Mortgage.CBResults.Liabilities.overdraftChangeLimitError"
          )}
          actions={option.none}
          title={option.none}
        />
      )}

      {(displayedFields.provider || displayedFields.contractNumber) && (
        <FormRow type="1-1">
          {displayedFields.provider ? (
            <OptionalEditDropdownField
              {...providerFieldProps}
              clearable={false}
              searchable
              label={formatMessage(
                !formSetup.isACPhase && formSetup.edit
                  ? "Liabilities.AddConsumerLoanOrMortgage.providerLabelOptional"
                  : "Liabilities.AddConsumerLoanOrMortgage.providerLabel"
              )}
              placeholder={formatMessage(
                "Liabilities.AddConsumerLoanOrMortgage.providerLabel"
              )}
              options={removeUCBProviderCode(
                props.providersList,
                formSetup.manuallyAdded
              )}
              value={selectedDropdownOption(
                providerFieldProps.value,
                props.providersList
              )}
              onChange={providerOnChange}
              edit={formSetup.edit}
              issues={formatOverdraftIssues(
                "provider",
                fieldProps("provider").issues
              )}
              feedback={formatOverdraftFeedback("provider")}
            />
          ) : null}

          {displayedFields.contractNumber ? (
            <OptionalEditTextField
              {...fieldProps("contractNumber")}
              value={pipe(
                fieldProps("contractNumber").value,
                option.getOrElse(constant(""))
              )}
              onChange={value =>
                pipe(
                  value,
                  option.fromPredicate(value => value.length > 0),
                  fieldProps("contractNumber").onChange
                )
              }
              label={formatMessage(
                !formSetup.isACPhase && formSetup.edit
                  ? "Mortgage.CBResults.Liabilities.contractNumberOptional"
                  : "Mortgage.CBResults.Liabilities.contractNumber"
              )}
              placeholder={formatMessage(
                "Mortgage.CBResults.Liabilities.contractNumber"
              )}
              edit={formSetup.edit}
              issues={formatOverdraftIssues(
                "contractNumber",
                fieldProps("contractNumber").issues
              )}
              feedback={formatOverdraftFeedback("contractNumber")}
            />
          ) : null}
        </FormRow>
      )}

      {displayedFields.newOverdraftLimit && (
        <FormRow type="1-1">
          <OptionalEditMoneyField
            {...fieldProps("newOverdraftLimit")}
            min={liabilitiesAmountLimits.min}
            max={liabilitiesAmountLimits.max}
            currency={currency}
            label={formatMessage(
              "Mortgage.CBResults.Liabilities.newOverdraftLimit"
            )}
            placeholder={formatMessage(
              "Mortgage.CBResults.Liabilities.newOverdraftLimit"
            )}
            edit={formSetup.edit}
            issues={formatOverdraftIssues(
              "newOverdraftLimit",
              fieldProps("newOverdraftLimit").issues
            )}
            feedback={formatOverdraftFeedback("newOverdraftLimit")}
          />
          {null}
        </FormRow>
      )}

      {!formSetup.edit && toFalse(values.changeLimit) && (
        <InlineMessage
          type="informative"
          size="medium"
          message={formatMessage(
            "Mortgage.CBResults.Liabilities.overdraftChangeLimitInfo"
          )}
        />
      )}
      {!formSetup.edit && toFalse(values.repaidBeforeDisbursement) ? (
        <InlineMessage
          type="informative"
          size="medium"
          message={formatMessage(
            `${
              props.variant === "CF" ? "StandardLoan" : "Mortgage"
            }.CBResults.Liabilities.repaidBeforeDisbursement` as LocaleKey
          )}
        />
      ) : null}

      {!formSetup.edit &&
      toFalse(
        pipe(
          props.initialValues,
          option.chain(i => i.usedForRefinance)
        )
      ) &&
      props.variant !== "OVD" ? (
        <InlineMessage
          type="informative"
          size="medium"
          message={formatMessage(
            "Mortgage.CBResults.Liabilities.ovd.selectedForRefinancing"
          )}
        />
      ) : null}

      {displayProductIsUsed()}

      {formSetup.isACPhase &&
        !formSetup.edit &&
        !checkRequiredFieldsProvided(
          values,
          getRequiredFields(values, formSetup, props.variant)
        ) && (
          <Banner
            type="warning"
            content={formatMessage(
              "Mortgage.CBResults.Liabilities.provideMandatoryDetailsWarning"
            )}
            onDismiss={option.none}
            actions={option.none}
            title={option.none}
          />
        )}

      {formSetup.edit ? (
        <ActionButtonGroup
          hAlignContent="right"
          actions={[
            {
              action: handleCancel,
              label: formatMessage("Cancel"),
              disabled: false,
              variant: "text",
            }, // as Action,
            {
              action: handleSubmit,
              labels: pipe(
                props.initialValues,
                option.fold(
                  () => ({
                    normal: formatMessage("Liabilities.Add"),
                    loading: formatMessage("Liabilities.Add"),
                    error: formatMessage("Liabilities.Add"),
                    success: formatMessage("Liabilities.Add"),
                  }),
                  () => ({
                    normal: formatMessage("Save"),
                    loading: formatMessage("Save"),
                    error: formatMessage("Save"),
                    success: formatMessage("Save"),
                  })
                )
              ),
              disabled: false,
              variant: "loading",
              type: "submit",
            }, // as Action
          ]}
        />
      ) : (
        <Box vAlignContent="center">
          <Space fluid />

          {formSetup.manuallyAdded && !props.isViewMode && (
            <Button
              disabled={props.isInteractingWithAnyForm}
              variant="text"
              size="default"
              label={formatMessage(
                "Mortgage.CBResults.Liabilities.removeLabel"
              )}
              action={props.onRemove}
            />
          )}
          <Space units={8} />
          {foldLiabilitiesVariant(props.variant, {
            onCf: () =>
              formSetup.manuallyAdded ? (
                <Button
                  disabled={props.isInteractingWithAnyForm}
                  variant="text"
                  size="default"
                  label={formatMessage(
                    "Mortgage.CBResults.Liabilities.editLabel"
                  )}
                  action={props.onEdit}
                />
              ) : null,
            onOvd: () =>
              formSetup.manuallyAdded ? (
                <Button
                  disabled={props.isInteractingWithAnyForm}
                  variant="text"
                  size="default"
                  label={formatMessage(
                    "Mortgage.CBResults.Liabilities.editLabel"
                  )}
                  action={props.onEdit}
                />
              ) : null,
            onMtg: () =>
              pipe(
                props.isViewMode,
                boolean.fold(
                  () => (
                    <Button
                      disabled={props.isInteractingWithAnyForm}
                      variant="text"
                      size="default"
                      label={formatMessage(
                        "Mortgage.CBResults.Liabilities.editLabel"
                      )}
                      action={props.onEdit}
                    />
                  ),
                  constNull
                )
              ),
          })}
        </Box>
      )}
    </FormSection>
  );
}
